/**
 * THE SIGNUP AGREEMENT — one tick, and one only.
 *
 * ============================================================================
 * ONE AGREEMENT TICK. Batch 7 §19: "There should be ONE agreement tick" and "Do not
 * create two confusing agreement checkboxes." This component renders exactly one
 * checkbox, and `getAllByRole('checkbox')` on the signup form returns exactly one.
 *
 * It gates the account: no tick, no signup. That rule is enforced in routes/Login.tsx,
 * not by a disabled button — see the note on `aria-required` below.
 * ============================================================================
 *
 * WHAT CHANGED, AND WHAT DID NOT
 * ------------------------------
 * There used to be TWO checkboxes here: the required Terms + Privacy acknowledgement,
 * and an optional marketing opt-in beside it. The second one is gone from this form.
 *
 * THE MARKETING PREFERENCE ITSELF IS NOT GONE, and that distinction is the whole design
 * of this change. Three things are true at once:
 *
 *   1. There is one tick on the form, which is what §19 asks for.
 *   2. Marketing consent is still a SEPARATE VALUE on the signup request —
 *      `ConsentRecord.marketingOptIn`, distinct from `acceptedLegal`, exactly as §19
 *      requires ("send the marketing-consent value separately from acceptance of the
 *      mandatory Terms if the backend needs to distinguish them"). The record's two
 *      fields are unchanged; see types/domain.ConsentRecord.
 *   3. That value is `false` at signup, because nobody was asked. Which is the correct
 *      value: consent that was never given is consent that is off. It was already
 *      `false` by default when the box existed, since it was not pre-ticked.
 *
 * So the checkbox was removed and the *capability* was not. The user turns product email
 * on in Settings, where there is already a live "Email updates" toggle
 * (routes/Settings.tsx, stores/preferencesStore.emailUpdates) — a control that existed
 * before this change and is the one the sentence below points at. Nothing was removed
 * from the product; one control was removed from one form, and the place that owns it is
 * named on the form itself.
 *
 * §19'S SENTENCE IS INCORPORATED INTO THE AGREEMENT PRESENTATION, which is what it asks
 * for — the brief's words are that the optional-marketing language "should be
 * incorporated into the Terms/Agreement presentation". It is inside the agreement box,
 * under the tick, as disclosure rather than as a second control: it tells the user what
 * the product will and will not email them, and where to change it, without asking them
 * to make a second decision at the moment they are trying to create an account.
 *
 * It is phrased to be TRUE OF A FORM WITH NO OPT-IN ON IT. The old sentence was "Send me
 * product updates and offers by email. Optional — you can change this at any time in
 * Settings…", which was a control's label; with no control it becomes a claim about a
 * checkbox that is not there. So it states the fact instead: product email is optional,
 * off by default, and turned on in Settings. Same three guarantees, none of them resting
 * on a tick.
 *
 * WHY THE AGREEMENT IS STILL BOXED AND TAGGED. Not to distinguish it from a neighbour any
 * more — it has none — but because it is the one thing on this form that is a legal
 * acknowledgement rather than a field. The box, the "Required" tag, and the form's own
 * refusal when it is missing are what make the tick read as a decision rather than as a
 * formality to click past.
 *
 * IT IS NOT PRE-CHECKED. `checked` is driven by the form's state, which starts false, and
 * `defaultChecked` appears nowhere in this file. A pre-ticked consent box does not collect
 * consent, it asserts it.
 *
 * THE LINKS ARE REAL LINKS to the public routes (/terms, /privacy), which render without
 * an account — see routes/Legal.tsx. `target="_blank"` so reading what you are agreeing
 * to does not destroy a half-filled form, which is the small thing that decides whether
 * anybody actually reads it.
 */
import { Link } from 'react-router-dom';
import { c } from '../../styles/theme';
import { paths } from '../../routes/paths';

/**
 * The marketing disclosure, verbatim, so the copy on screen and the copy a test asserts
 * are the same string.
 *
 * Exported because this sentence is a requirement rather than a piece of styling — §19
 * specifies what it has to communicate — and a requirement that lives only inside JSX is
 * one a refactor can quietly reword.
 */
export const MARKETING_DISCLOSURE =
  'Product updates and offers by email are optional and off by default — you can turn ' +
  'them on at any time in Settings, and it has no effect on your account.';

export interface ConsentFieldsProps {
  /** The required Terms + Privacy acknowledgement. Starts false. */
  acceptedLegal: boolean;
  onAcceptedLegalChange: (value: boolean) => void;
  /** Disables the tick while a submission is in flight. */
  disabled?: boolean;
  /**
   * Marks the tick as failing validation.
   *
   * Set by the form when a submit was attempted without it, so the box turns red at the
   * moment the refusal is explained rather than colouring itself while the user is still
   * filling the form in. An untouched checkbox is not an error.
   */
  invalid?: boolean;
}

/** The checkbox itself. */
const BOX_STYLE = {
  width: 15,
  height: 15,
  margin: 0,
  flexShrink: 0,
  // Aligns the box with the cap height of the first line rather than its box centre,
  // which is what makes a two-line label look attached to its control.
  marginTop: 1,
  accentColor: c.accentFill,
  cursor: 'pointer',
} as const;

/** A link to one of the public legal documents, styled as inline body text. */
function LegalLink({ to, children }: { to: string; children: string }) {
  return (
    <Link
      to={to}
      // Opens beside the form rather than replacing it: navigating away from a
      // half-completed signup to read the terms and then having to start again is how a
      // consent link becomes one nobody follows.
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: c.accent, textDecoration: 'underline' }}
    >
      {children}
    </Link>
  );
}

export default function ConsentFields({
  acceptedLegal,
  onAcceptedLegalChange,
  disabled,
  invalid,
}: ConsentFieldsProps) {
  return (
    <div style={{ marginTop: 14 }}>
      {/* ---- THE ONE TICK. Boxed, tagged, and gating the submit. ------------------- */}
      <div
        style={{
          border: `1px solid ${invalid ? c.danger : c.border}`,
          background: c.surface2,
          borderRadius: 8,
          padding: '10px 11px',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 9,
            fontSize: 11.5,
            lineHeight: 1.55,
            color: c.text2,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            // NOT pre-checked: `checked` is driven by the form's state, which starts
            // false. There is no defaultChecked anywhere in this file.
            checked={acceptedLegal}
            onChange={(e) => onAcceptedLegalChange(e.target.checked)}
            disabled={disabled}
            /*
              `aria-required` BUT NOT THE HTML `required` ATTRIBUTE, which is a choice
              worth stating because the attribute looks like the obviously correct thing
              to add.

              A `required` checkbox triggers the browser's own interactive validation on
              submit, which cancels the submit event — so `handleSubmit` would never run,
              the form's own refusal ("Please accept the Terms of Service and Privacy
              Policy…") would never be set, the box would never turn red, and the whole
              message would be a native bubble reading "Please tick this box if you want
              to proceed". That bubble is less informative, is not in a live region, and
              disappears on the next click. Two validations competing means only the
              coarser one is ever seen.

              So the requirement is expressed to assistive technology here and enforced
              in routes/Login.tsx, which is the one that can also explain itself.
            */
            aria-required="true"
            aria-invalid={invalid ? true : undefined}
            style={BOX_STYLE}
          />
          <span>
            I have read and agree to the <LegalLink to={paths.terms}>Terms of Service</LegalLink> and{' '}
            <LegalLink to={paths.privacy}>Privacy Policy</LegalLink>.{' '}
            {/* The tag sits inside the label rather than beside it, so a screen reader
                reads "…Privacy Policy. Required" as one sentence. */}
            <span
              style={{
                fontSize: 9,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: c.accent,
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              Required
            </span>
          </span>
        </label>

        {/*
          ---- THE MARKETING DISCLOSURE, INSIDE THE AGREEMENT AND NOT A CONTROL ----

          §19 asks for the optional-marketing language to be incorporated into the
          agreement presentation, and this is that: a statement of what the product does,
          under the tick it belongs to, with no second decision attached.

          NOT A LABEL WITHOUT ITS INPUT. It deliberately does not say "Send me product
          updates" — there is nothing here to send them with. It says the email is
          optional, off unless asked for, and switched on in Settings, which are the three
          things the removed checkbox guaranteed.

          A `<p>` inside the box rather than after it, so it reads as part of what is being
          agreed to rather than as a footnote about a different subject.

          text2 and 10.5px: a step down in size from the agreement above it, and NOT a step
          down in colour. text3 measures below AA on every surface in this app, and a
          disclosure nobody can read is not a disclosure — see styles/theme.ts, and
          contrast.test.ts, which enforces this at the call site.
        */}
        <p
          style={{
            fontSize: 10.5,
            color: c.text2,
            margin: '9px 0 0',
            paddingTop: 9,
            // A hairline rather than only space: it separates a statement of fact from an
            // acknowledgement without making it look like a second row of a list.
            borderTop: `1px solid ${c.border}`,
            lineHeight: 1.5,
          }}
        >
          {MARKETING_DISCLOSURE}
        </p>
      </div>

    </div>
  );
}
